/**
 * The auth family's shared material — extracted from AuthScreen so the
 * ancillary pages (forgot/reset password, email verification) render the
 * exact same glass over the same aurora. Edit here, not per-page.
 */

export const AUTH_EASE = [0.22, 1, 0.36, 1] as const;

export const makeAuthColors = (isDark: boolean) => ({
  textPrimary: isDark ? "#ffffff" : "#1d1d1f",
  textSecondary: isDark ? "#9ca3af" : "#64748b",
  greenLink: isDark ? "#4ade80" : "#16a34a",
});

// Glass fields over the aurora — translucent fill + blur instead of the
// theme's solid grey, with the autofill paint suppressed (it would punch an
// opaque yellow/grey slab through the glass).
export const makeGlassFieldSx = (isDark: boolean) => {
  const textPrimary = isDark ? "#ffffff" : "#1d1d1f";
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(255, 255, 255, 0.5)",
      backdropFilter: "blur(12px)",
      transition: "background-color 0.2s ease",
      "& fieldset": {
        borderColor: isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.12)",
        transition: "border-color 0.2s ease",
      },
      "&:hover fieldset": {
        borderColor: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.25)",
      },
      "&.Mui-focused": {
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.09)" : "rgba(255, 255, 255, 0.65)",
      },
      "&.Mui-focused fieldset": { borderColor: "#22c55e" },
    },
    "& input:-webkit-autofill": {
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: textPrimary,
      caretColor: textPrimary,
      transition: "background-color 5000s ease-in-out 0s",
    },
  } as const;
};

export const primaryButtonSx = {
  backgroundColor: "#22c55e",
  color: "#ffffff",
  fontWeight: 700,
  textTransform: "none",
  height: 48,
  fontSize: "1rem",
  borderRadius: 2.5,
  transition: "background-color 0.15s ease, transform 0.1s ease",
  "&:active": { transform: "scale(0.98)" },
  "&:hover": { backgroundColor: "#16a34a" },
  "&:disabled": {
    backgroundColor: "rgba(34, 197, 94, 0.35)",
    color: "#ffffff",
  },
} as const;

// Secondary actions share the fields' glass material.
export const makeGlassButtonSx = (isDark: boolean) =>
  ({
    height: 44,
    fontWeight: 600,
    fontSize: "0.875rem",
    textTransform: "none",
    whiteSpace: "nowrap",
    borderRadius: 2.5,
    color: isDark ? "#ffffff" : "#1d1d1f",
    backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(255, 255, 255, 0.5)",
    backdropFilter: "blur(12px)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    transition: "background-color 0.15s ease, border-color 0.15s ease, transform 0.1s ease",
    "&:active": { transform: "scale(0.97)" },
    "&:hover": {
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.7)",
      borderColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.18)",
    },
  }) as const;
